import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IoClose,
  IoBarcodeOutline,
  IoCheckmarkCircle,
  IoArrowForward,
  IoRefreshOutline,
  IoAddCircleOutline,
  IoTrashOutline,
} from "react-icons/io5";
import api from "@/api";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { ExpiryDateInput } from "@/components/ExpiryDateInput";
import { tapSuccess, tapError, tapLight } from "@/utils/haptics";

const MAX_LOTS = 3;
const makeEmptyLot = () => ({ expiryDate: "", quantity: 1 });

/**
 * Continuous "Multi-Add" wizard: chains BarcodeScanner → ExpiryOCR → save → loop.
 * State machine: barcode → expiry → save → barcode …
 * Exits when user taps "Fine".
 * Supports up to 3 separate expiry lots per scan (mixed box).
 */
export const MultiAddWizard = ({ open, storeId, onClose }) => {
  const [stage, setStage] = useState("barcode"); // barcode | expiry | saving
  const [current, setCurrent] = useState(null); // {barcode, name, image_url, image_file_id, price, cost, existingId}
  const [lots, setLots] = useState([makeEmptyLot()]);
  const [addedCount, setAddedCount] = useState(0);
  const [lastAdded, setLastAdded] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setStage("barcode");
      setCurrent(null);
      setLots([makeEmptyLot()]);
      setAddedCount(0);
      setLastAdded("");
      setSaving(false);
    }
  }, [open]);

  const handleDetected = async (code) => {
    // Called inside BarcodeScanner; move to expiry step.
    // Prevent duplicate captures.
    if (stage !== "barcode") return;
    setStage("expiry");
    tapLight();
    // Prefill using catalog lookup for the current store, then generic lookup.
    let payload = { barcode: code, name: "", image_url: "", image_file_id: "", price: "", cost: "", notes: "", existingId: null };
    try {
      const { data } = await api.get(`/products/scan/${encodeURIComponent(code)}`, { params: { store_id: storeId } });
      payload = {
        ...payload,
        existingId: data.id,
        name: data.name || "",
        image_url: data.image_url || "",
        image_file_id: data.image_file_id || "",
        price: data.price ?? "",
        cost: data.cost ?? "",
      };
    } catch {
      try {
        const { data } = await api.get(`/products/lookup/${encodeURIComponent(code)}`);
        payload = {
          ...payload,
          name: data?.name || "",
          image_url: data?.image_url || "",
          image_file_id: data?.image_file_id || "",
          price: data?.price ?? "",
          cost: data?.cost ?? "",
          notes: data?.notes || "",
        };
      } catch { /* new item */ }
    }
    setCurrent(payload);
    setLots([makeEmptyLot()]);
  };

  const addLot = () => {
    if (lots.length >= MAX_LOTS) return;
    tapLight();
    setLots((arr) => [...arr, makeEmptyLot()]);
  };
  const removeLot = (index) => {
    if (index === 0) return; // never remove the primary lot
    tapLight();
    setLots((arr) => arr.filter((_, i) => i !== index));
  };
  const updateLot = (index, patch) => {
    setLots((arr) => arr.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const saveAndLoop = async () => {
    if (!current) return;
    // Validate all lots.
    const cleaned = lots.map((l, i) => ({
      index: i,
      expiryDate: (l.expiryDate || "").trim(),
      quantity: Math.max(1, Number(l.quantity) || 1),
    }));
    const missing = cleaned.find((l) => !l.expiryDate);
    if (missing) {
      toast.error(`Inserisci la data di scadenza per il lotto ${missing.index + 1}`);
      return;
    }
    // Guard against duplicate expiry dates within the same submission (they'd just
    // increment each other server-side, which is confusing here).
    const seen = new Set();
    for (const l of cleaned) {
      if (seen.has(l.expiryDate)) {
        toast.error("Le date dei lotti devono essere diverse tra loro");
        return;
      }
      seen.add(l.expiryDate);
    }

    setSaving(true);
    setStage("saving");
    const nameFallback = current.name?.trim() || `EAN ${current.barcode}`;
    const baseBody = {
      store_id: storeId,
      barcode: current.barcode,
      name: nameFallback,
      image_url: current.image_url || undefined,
      image_file_id: current.image_file_id || undefined,
      price: current.price === "" || current.price == null ? undefined : Number(current.price),
      cost: current.cost === "" || current.cost == null ? undefined : Number(current.cost),
      create_new_lot: true,
    };
    let ok = 0;
    let totalQty = 0;
    let firstError = null;
    for (const l of cleaned) {
      try {
        await api.post("/products/quick-upsert", {
          ...baseBody,
          expiry_date: l.expiryDate,
          quantity: l.quantity,
        });
        ok += 1;
        totalQty += l.quantity;
      } catch (e) {
        if (!firstError) firstError = e?.response?.data?.detail || "Errore salvataggio";
      }
    }
    setSaving(false);
    if (ok === 0) {
      tapError();
      toast.error(firstError || "Errore salvataggio");
      setStage("expiry");
      return;
    }
    tapSuccess();
    const lotLabel = ok === 1 ? "lotto" : "lotti";
    setLastAdded(`${nameFallback} · ${ok} ${lotLabel} (${totalQty} pz)`);
    setAddedCount((n) => n + ok);
    if (firstError) toast.error(firstError);
    // Reset for next scan
    setCurrent(null);
    setLots([makeEmptyLot()]);
    setStage("barcode");
  };

  const skipCurrent = () => {
    setCurrent(null);
    setLots([makeEmptyLot()]);
    setStage("barcode");
  };

  if (!open) return null;

  return (
    <div className="tf-multiadd-root" data-testid="multiadd-wizard">
      {stage === "barcode" && (
        <BarcodeScanner
          open={true}
          onClose={onClose}
          onDetected={handleDetected}
        />
      )}

      {/* Floating counter always visible while wizard active */}
      {stage === "barcode" && addedCount > 0 && (
        <div className="tf-multiadd-counter" data-testid="multiadd-counter">
          <IoCheckmarkCircle size={18} />
          <span>{addedCount} aggiunt{addedCount === 1 ? "o" : "i"}</span>
          {lastAdded && <span className="tf-multiadd-last">· {lastAdded}</span>}
        </div>
      )}

      {(stage === "expiry" || stage === "saving") && current && (
        <div className="tf-sheet-backdrop" data-testid="multiadd-expiry-sheet">
          <div className="tf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="tf-between" style={{ marginBottom: 8 }}>
              <div className="tf-h2" style={{ margin: 0 }}>
                <IoBarcodeOutline size={20} style={{ verticalAlign: "-4px", marginRight: 6 }} />
                {current.name || `EAN ${current.barcode}`}
              </div>
              <button
                type="button"
                data-testid="multiadd-skip"
                className="tf-close-btn"
                onClick={skipCurrent}
                aria-label="Salta"
                title="Salta"
              >
                <IoClose size={20} />
              </button>
            </div>
            <div className="tf-small" style={{ color: "var(--tf-muted)", marginBottom: 12 }}>
              {current.existingId ? "Prodotto già in catalogo — verrà aggiunto un nuovo lotto" : `Codice ${current.barcode}`}
            </div>

            {!current.existingId && (
              <div className="tf-field">
                <label className="tf-label">Nome prodotto (opzionale)</label>
                <input
                  className="tf-input"
                  data-testid="multiadd-name"
                  value={current.name || ""}
                  onChange={(e) => setCurrent((c) => ({ ...c, name: e.target.value }))}
                  placeholder={`EAN ${current.barcode}`}
                />
              </div>
            )}

            {lots.map((lot, idx) => {
              const canRemove = idx > 0;
              return (
                <div
                  key={idx}
                  data-testid={`multiadd-lot-${idx}`}
                  style={{
                    border: idx === 0 ? "none" : "1px dashed var(--tf-border, #d1d5db)",
                    borderRadius: idx === 0 ? 0 : 12,
                    padding: idx === 0 ? 0 : "12px 12px 4px",
                    marginTop: idx === 0 ? 0 : 12,
                    background: idx === 0 ? "transparent" : "rgba(0,0,0,0.02)",
                    position: "relative",
                  }}
                >
                  {lots.length > 1 && (
                    <div
                      className="tf-small"
                      style={{ color: "var(--tf-muted)", fontWeight: 600, marginBottom: 6 }}
                    >
                      Lotto {idx + 1}
                    </div>
                  )}
                  {canRemove && (
                    <button
                      type="button"
                      data-testid={`multiadd-remove-lot-${idx}`}
                      onClick={() => removeLot(idx)}
                      aria-label={`Rimuovi lotto ${idx + 1}`}
                      title="Rimuovi lotto"
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        border: "none",
                        background: "transparent",
                        color: "var(--tf-muted)",
                        cursor: "pointer",
                        padding: 4,
                      }}
                    >
                      <IoTrashOutline size={18} />
                    </button>
                  )}

                  <div className="tf-field">
                    <label className="tf-label">Data di scadenza *</label>
                    <ExpiryDateInput
                      value={lot.expiryDate}
                      onChange={(v) => updateLot(idx, { expiryDate: v })}
                      testIdPrefix={`multiadd-expiry-${idx}`}
                    />
                  </div>

                  <div className="tf-field">
                    <label className="tf-label">Quantità</label>
                    <div className="tf-qty-quick-stepper" style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        data-testid={`multiadd-minus-${idx}`}
                        className="tf-qty-quick-btn"
                        disabled={(lot.quantity || 0) <= 1}
                        onClick={() => updateLot(idx, { quantity: Math.max(1, (Number(lot.quantity) || 1) - 1) })}
                      >−</button>
                      <input
                        type="number"
                        className="tf-qty-quick-value"
                        data-testid={`multiadd-qty-${idx}`}
                        value={lot.quantity}
                        min={1}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") { updateLot(idx, { quantity: "" }); return; }
                          const n = Number(raw);
                          if (Number.isFinite(n) && n >= 0) updateLot(idx, { quantity: n });
                        }}
                        onBlur={() => {
                          if (lot.quantity === "" || Number(lot.quantity) < 1) updateLot(idx, { quantity: 1 });
                        }}
                        style={{ background: "transparent", border: "none", outline: "none" }}
                      />
                      <button
                        type="button"
                        data-testid={`multiadd-plus-${idx}`}
                        className="tf-qty-quick-btn"
                        onClick={() => updateLot(idx, { quantity: (Number(lot.quantity) || 0) + 1 })}
                      >+</button>
                    </div>
                  </div>
                </div>
              );
            })}

            {lots.length < MAX_LOTS && (
              <button
                type="button"
                data-testid="multiadd-add-lot"
                onClick={addLot}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "transparent",
                  border: "1px dashed var(--tf-brand, #E2001A)",
                  color: "var(--tf-brand, #E2001A)",
                  padding: "10px 12px",
                  borderRadius: 10,
                  width: "100%",
                  justifyContent: "center",
                  marginTop: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <IoAddCircleOutline size={18} />
                Aggiungi altra data ({lots.length}/{MAX_LOTS})
              </button>
            )}

            <button
              type="button"
              data-testid="multiadd-save"
              className="tf-btn-primary"
              disabled={saving || lots.some((l) => !l.expiryDate)}
              onClick={saveAndLoop}
              style={{ marginTop: 12 }}
            >
              {saving ? <IoRefreshOutline size={18} className="tf-spin" /> : <IoArrowForward size={18} />}
              {saving ? "Salvo…" : lots.length > 1 ? `Salva ${lots.length} lotti` : "Salva e continua"}
            </button>
            <button
              type="button"
              data-testid="multiadd-finish"
              className="tf-btn-ghost"
              onClick={onClose}
              style={{ marginTop: 8 }}
            >
              Fine ({addedCount} aggiunt{addedCount === 1 ? "o" : "i"})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
